import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Button, CircularProgress, Grid, IconButton, InputAdornment, Link as MuiLink, TextField, Typography } from '@mui/material'
import { GuestLayout } from 'components/_template'
import { useAuth } from 'context'
import { SignInDto, signInValidationSchema } from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'
import { NavLink, Navigate, useLocation } from 'react-router-dom'

export default function SignIn() {
  const { signIn, user, status } = useAuth()
  const location = useLocation()

  const [showPassword, setShowPassword] = useState<boolean>(false)

  useEffect(() => {
    document.title = 'Sign In | Vehicle Tracking System'
  }, [])

  return (
    <>
      {user && status === 'authorized' && <Navigate to={location.state?.from ?? '/'} />}

      {status === 'loading' && (
        <Grid container spacing={2} direction='column' alignItems='center' justifyContent='center' style={{ minHeight: '100vh' }}>
          <Grid item>
            <Typography variant='h4' fontWeight={600} display='block'>
              Vehicle Tracking System
            </Typography>
          </Grid>

          <Grid item>
            <CircularProgress size={40} />
          </Grid>
        </Grid>
      )}

      {status === 'unauthorized' && (
        <Formik
          enableReinitialize
          initialValues={new SignInDto()}
          validateOnChange
          validateOnBlur
          validationSchema={signInValidationSchema}
          onSubmit={async values => {
            const { email, password } = values
            await signIn({ email, password })
          }}
        >
          {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => {
            return (
              <GuestLayout handleSubmit={handleSubmit} title=''>
                <Typography variant='h6' fontWeight={500} align='center'>
                  Welcome to Vehicle Tracking System!
                </Typography>

                <TextField
                  focused
                  autoComplete='email'
                  disabled={isSubmitting}
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  id='sign-in-email'
                  InputLabelProps={{
                    shrink: true
                  }}
                  InputProps={{ style: { color: 'black' } }}
                  label='Email Address'
                  margin='normal'
                  name='email'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email || ''}
                />

                <TextField
                  focused
                  autoComplete='current-password'
                  disabled={isSubmitting}
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  id='sign-in-password'
                  InputLabelProps={{
                    shrink: true
                  }}
                  InputProps={{
                    style: { color: '#000' },
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={_ => setShowPassword(!showPassword)}
                          onMouseDown={e => e.preventDefault()}
                          edge='end'
                          style={{ color: '#000' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  label='Password'
                  margin='normal'
                  name='password'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type={showPassword ? 'text' : 'password'}
                  value={values.password || ''}
                />

                <Button disabled={isSubmitting} fullWidth id='sign-in-submit' type='submit' variant='contained'>
                  SIGN IN
                </Button>

                <Typography align='center'>
                  <MuiLink component={NavLink} sx={{ textDecoration: 'none' }} to='/account/forgot-password'>
                    Forgot password?
                  </MuiLink>
                </Typography>
              </GuestLayout>
            )
          }}
        </Formik>
      )}
    </>
  )
}
